import { Component, Input, ViewChild, ElementRef, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject, HostListener, AfterViewInit, OnChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { concatMap, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MobileResolutionService } from 'src/app/services/platform/mobile-resolution.service';
import { ConsoleLoggerService } from 'src/app/services/logging/console-logger.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-ev-slider',
  standalone: true,
  templateUrl: './ev-slider.component.html',
  styleUrls: ['./ev-slider.component.scss'],
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EvSliderComponent implements AfterViewInit, OnChanges {

  // input output data
  @Input() altString!: string;
  @Input() digit!: number;
  @Input() min!: number;
  @Input() max!: number;
  @Input() value!: number;
  @Output() valueDragEvent = new EventEmitter();
  @Output() valueChangeEvent = new EventEmitter();

  @ViewChild('sliderOuter', { static: true }) slider!: ElementRef;

  // recalculating sliders when size changed
  @HostListener('window:resize') onResize() {
    this.setSliderToCurrValue();
  }

  private sliderFill!: HTMLElement;
  private sliderImg!: HTMLElement;
  private readonly destroyRef = inject(DestroyRef);
  public valueInPercent!: number;

  constructor(public matDialog: MatDialog,
    public mobileResolutionService: MobileResolutionService, private readonly consoleLoggerService: ConsoleLoggerService,
    public settingsService: SettingsService, private readonly cdr: ChangeDetectorRef, private readonly utilsService: UtilsService) {
  }

  ngOnChanges(): void {
    this.setSliderToCurrValue();
  }

  // subscribe slider move/touch events, setting slider to the default values
  ngAfterViewInit(): void {
    const sDown = fromEvent<MouseEvent>(this.slider.nativeElement, 'mousedown');
    const sMove = fromEvent<MouseEvent>(document, 'mousemove');
    const sUp = fromEvent<MouseEvent>(document, 'mouseup');
    const sDrag = sDown.pipe(concatMap(DragEvent => sMove.pipe(takeUntil(sUp))));

    let dragged = false;
    // set while a press that started on this slider is in progress
    let pressedOnSlider = false;
    sDown.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => { pressedOnSlider = true; });

    sUp.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((md: MouseEvent) => {
      if (pressedOnSlider) {
        this.moveSlider(md.pageX);
      }
      dragged = false;
      pressedOnSlider = false;
    });

    sDrag.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((md: MouseEvent) => {
      dragged = true;
      if (!Number.isNaN(md.pageX)) {
        this.moveSliderVisual(md.pageX);
      }
    });

    const sTouchStart = fromEvent<TouchEvent>(this.slider.nativeElement, 'touchstart');
    const sTouchMove = fromEvent<TouchEvent>(this.slider.nativeElement, 'touchmove', { passive: false });
    const sTouchEnd = fromEvent<TouchEvent>(this.slider.nativeElement, 'touchend');
    const sTouchDrag = sTouchStart.pipe(concatMap(DragEvent => sTouchMove.pipe(takeUntil(sTouchEnd))));

    let touchStartX = 0;
    let touchStartY = 0;
    let directionLocked: 'horizontal' | 'vertical' | null = null;

    sTouchStart.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((td: TouchEvent) => {
      pressedOnSlider = true;
      touchStartX = td.touches[0].pageX;
      touchStartY = td.touches[0].pageY;
      directionLocked = null;
    });
    sTouchEnd.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((td: TouchEvent) => {
      if (!dragged && pressedOnSlider && directionLocked !== 'vertical') {
        this.moveSlider(td.changedTouches[0].pageX);
      }
      dragged = false;
      pressedOnSlider = false;
      directionLocked = null;
    });

    sTouchDrag.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((td: TouchEvent) => {
      const currentX = td.changedTouches[0].pageX;
      const currentY = td.changedTouches[0].pageY;

      if (directionLocked === null) {
        const deltaX = Math.abs(currentX - touchStartX);
        const deltaY = Math.abs(currentY - touchStartY);
        if (deltaX > 10 || deltaY > 10) {
          directionLocked = deltaX >= deltaY ? 'horizontal' : 'vertical';
        }
      }

      if (directionLocked !== 'horizontal') {
        return;
      }

      if (td.cancelable) { td.preventDefault(); }
      dragged = true;
      if (!Number.isNaN(currentX)) {
        this.moveSlider(currentX);
      }
    });

    this.sliderFill = this.slider.nativeElement.querySelector('.slider-fill');
    this.sliderImg = this.slider.nativeElement.querySelector('.slider-thumb');
    this.setSliderToCurrValue();

    this.mobileResolutionService.ObservableMobileVisiblePanel.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp == "car") {
        this.setSliderToCurrValue();
      }
    });
  }

  // setting slider to the current value
  public setSliderToCurrValue(): void {
    this.valueInPercent = Math.round((this.value - this.min) / (this.max - this.min) * 100);
  }

  private moveSlider(pageX: number): void {
    // The inner track is inset by half the thumb size on each side,
    // so the usable range maps exactly to the inner element's width.
    const inner = this.slider.nativeElement.querySelector('.slider-track-inner') as HTMLElement;
    const rect = inner.getBoundingClientRect();
    let ratio = (pageX - rect.left) / rect.width;

    // clamp 0..1
    ratio = Math.max(0, Math.min(1, ratio));

    // compute value and round to digit precision
    let value = ratio * (this.max - this.min) + this.min;
    value = +this.toFixed(value, this.digit);
    value = Math.max(this.min, Math.min(this.max, value));

    this.changeValue(value);
  }

  // Update visual position only (no emit to parent)
  private moveSliderVisual(pageX: number): void {
    const inner = this.slider.nativeElement.querySelector('.slider-track-inner') as HTMLElement;
    const rect = inner.getBoundingClientRect();
    let ratio = (pageX - rect.left) / rect.width;
    ratio = Math.max(0, Math.min(1, ratio));

    // compute value and round to digit precision
    let value = ratio * (this.max - this.min) + this.min;
    value = +this.toFixed(value, this.digit);
    value = Math.max(this.min, Math.min(this.max, value));

    this.value = value;
    this.valueDragEvent.emit(Number.parseFloat(value.toFixed(this.digit)));
    this.setSliderToCurrValue();
    this.cdr.markForCheck();
  }

  // create number with fixed decimals
  private toFixed(num, precision): string {
    return this.utilsService.toFixed(num, precision);
  }

  // passing slider new value to parent
  private changeValue(value: number): void {
    this.value = value;
    this.valueDragEvent.emit(Number.parseFloat(value.toFixed(this.digit)));
    this.valueChangeEvent.emit();
  }
}
